/**
 * Global client-error reporting (#751 Ops-Härtung, Teil 3).
 *
 * The bug reporter is manual — unhandled exceptions were invisible in ops.
 * initErrorReporting() hooks window `error` + `unhandledrejection` and POSTs
 * a compact, PII-free report to /api/client-errors (structured server log +
 * Prometheus counter per screen).
 *
 * Safety properties, in order of importance:
 *   - never throws and never re-reports its own failures (silent catch)
 *   - max 5 reports per page session, deduped by message — an error loop in
 *     a render path must not turn into request spam (server backstops at
 *     10/min per IP anyway)
 *   - skips silently when there is no JWT (endpoint requires auth)
 *   - message/stack truncated client-side to the server's schema limits
 */
import { reportClientError } from '../api';
import { getJwt } from '../auth';

declare const __APP_VERSION__: string;

const MAX_REPORTS_PER_SESSION = 5;
const MAX_MESSAGE = 500;
const MAX_STACK = 4000;

let sentCount = 0;
const seenMessages = new Set<string>();

/** Test-only: reset throttle/dedupe state between cases. */
export function resetErrorReportingForTest(): void {
  sentCount = 0;
  seenMessages.clear();
}

/** Last path segment of the hash → screen id ("#SID/planning" → "planning",
 *  "#hub" → "hub"). Server-side sanitiser collapses anything odd to
 *  "unknown", so this only needs to be roughly right. */
function currentScreenId(): string {
  const raw = (location.hash || '').replace(/^#/, '').split('?')[0];
  if (!raw) return 'unknown';
  const seg = raw.split('/').pop() || raw;
  return seg.toLowerCase();
}

function appVersion(): string {
  return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';
}

/**
 * Report one error. Exported for the two listeners + tests; all guards live
 * here so both event paths behave identically.
 */
export function captureError(message: string, stack: string, source: string): void {
  try {
    const msg = (message || '').slice(0, MAX_MESSAGE);
    if (!msg) return;
    if (seenMessages.has(msg)) return;
    if (sentCount >= MAX_REPORTS_PER_SESSION) return;
    if (!getJwt()) return;
    seenMessages.add(msg);
    sentCount++;
    reportClientError({
      message: msg,
      stack: (stack || '').slice(0, MAX_STACK),
      screen: currentScreenId(),
      clientVersion: appVersion(),
      source,
    }).catch(() => {
      /* fire-and-forget — reporting must never cause follow-up errors */
    });
  } catch {
    /* never throw from the error path */
  }
}

/** Hook global handlers. Call once from boot(). */
export function initErrorReporting(): void {
  window.addEventListener('error', (ev: ErrorEvent) => {
    // Resource-load errors (img/script) fire `error` without a message —
    // those are asset/CDN issues, not code errors; skip them.
    if (!ev.message) return;
    captureError(ev.message, ev.error?.stack ?? '', 'onerror');
  });
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const r: unknown = ev.reason;
    if (r instanceof Error) {
      captureError(r.message, r.stack ?? '', 'unhandledrejection');
    } else {
      captureError(String(r ?? ''), '', 'unhandledrejection');
    }
  });
}
